import { sha256 as nobleSha256 } from '@noble/hashes/sha2'
import { sha512 } from '@noble/hashes/sha512'
import { hmac as nobleHmac } from '@noble/hashes/hmac'
import { hkdf } from '@noble/hashes/hkdf'
import * as ed from '@noble/ed25519'
import { xchacha20poly1305 } from '@noble/ciphers/chacha.js'
import { recordBrowserHttpErrorResponse } from './browserHttpErrors'
import { sdkConsole } from './logger'

// Initialize ed25519 with sha512 for browser environments (v2.x)
ed.etc.sha512Sync = (...m) => sha512(ed.etc.concatBytes(...m))
ed.etc.sha512Async = (...m) => Promise.resolve(sha512(ed.etc.concatBytes(...m)))
import type { CryptoProvider, HeadUpdateResult, RemoteStore, SyncScheduler } from './types'

const encoder = new TextEncoder()

/** Ephemeral storage header for E2E tests (mirrors storeBackends.ts). */
function getEphemeralHeaders(): Record<string, string> {
    const secret = typeof window !== 'undefined' && (window as any).__e2eStorageSecret
    if (secret) {
        return { 'X-Storage-Mode': `memory:${secret}` }
    }
    return {}
}

/**
 * Coerce any Uint8Array to one backed by a standalone ArrayBuffer.
 * See vault-crypto.ts for rationale (TS 5.7+ generic Uint8Array).
 */
function toAB(src: Uint8Array): Uint8Array<ArrayBuffer> {
    if (src.buffer instanceof ArrayBuffer && src.byteOffset === 0 && src.byteLength === src.buffer.byteLength) {
        return src as Uint8Array<ArrayBuffer>
    }
    return new Uint8Array(src) as Uint8Array<ArrayBuffer>
}


function delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms))
}

function isRetryableHttpStatus(status: number): boolean {
    return status === 408 || status === 425 || status === 429 || status >= 500
}

function isAbortError(error: unknown): boolean {
    return typeof error === 'object'
        && error !== null
        && (error as { name?: unknown }).name === 'AbortError'
}

async function fetchWithRetry(
    fn: () => Promise<Response>,
    meta: { url: string; method: string },
    maxAttempts = 7,
): Promise<Response> {
    let lastError: unknown = null
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
            const res = await fn()
            recordBrowserHttpErrorResponse(res, meta)
            if (!isRetryableHttpStatus(res.status) || attempt >= maxAttempts) {
                return res
            }
            lastError = new Error(`${meta.method} ${meta.url} failed with ${res.status}`)
        } catch (error) {
            if (isAbortError(error)) {
                throw error
            }
            lastError = error
            if (attempt >= maxAttempts) break
        }
        await delay(Math.min(10_000, 500 * (2 ** (attempt - 1))))
    }
    throw lastError instanceof Error ? lastError : new Error('Request failed')
}

function concatBytes(a: Uint8Array, b: Uint8Array): Uint8Array {
    const out = new Uint8Array(a.length + b.length)
    out.set(a, 0)
    out.set(b, a.length)
    return out
}

function toHex(bytes: Uint8Array): string {
    return Array.from(bytes).map(byte => byte.toString(16).padStart(2, '0')).join('')
}

function sha256Hex(bytes: Uint8Array): string {
    return toHex(nobleSha256(bytes))
}

const OBJECT_BATCH_GET_LIMIT = 100
const ATTRIBUTION_CLIENT_HEADER = 'x-secureblob-client'
const ATTRIBUTION_WORKLOAD_HEADER = 'x-secureblob-workload'
const ATTRIBUTION_WORKSPACE_TYPE_HEADER = 'x-secureblob-workspace-type'

function attributionHeaders(workload: 'workspace-fs' | 'sqlite3mt' = 'workspace-fs'): Record<string, string> {
    return {
        [ATTRIBUTION_CLIENT_HEADER]: 'js-sdk',
        [ATTRIBUTION_WORKLOAD_HEADER]: workload,
        [ATTRIBUTION_WORKSPACE_TYPE_HEADER]: 'normal',
    }
}

export class WebCryptoProvider implements CryptoProvider {
    async sha256(data: Uint8Array): Promise<string> {
        if (!ArrayBuffer.isView(data)) throw new Error('sha256: data is not Uint8Array')
        return toHex(nobleSha256(data))
    }

    async sha256Bytes(data: Uint8Array): Promise<Uint8Array> {
        if (!ArrayBuffer.isView(data)) throw new Error('sha256Bytes: data is not Uint8Array')
        return nobleSha256(data)
    }

    /**
     * Encrypt using XChaCha20-Poly1305 (Spec §4.2).
     * Output: nonce(24) || ciphertext+tag
     */
    async encrypt(key: Uint8Array, plaintext: Uint8Array, aad?: Uint8Array): Promise<Uint8Array> {
        if (!ArrayBuffer.isView(key)) throw new Error('encrypt: key is not Uint8Array')
        if (!ArrayBuffer.isView(plaintext)) throw new Error('encrypt: plaintext is not Uint8Array')
        const nonce = crypto.getRandomValues(new Uint8Array(24))
        const cipher = xchacha20poly1305(key, nonce, aad)
        const sealed = cipher.encrypt(plaintext)
        return concatBytes(nonce, sealed)
    }

    /**
     * Content-addressed deterministic encryption (Spec §11 v3).
     * Derives nonce from content to ensure same content → same ciphertext → same hash.
     * Internally handles key separation: nonceKey = HKDF(key, 'nonce-derivation').
     * Output: nonce(24) || ciphertext+tag (same format as encrypt).
     */
    async encryptDeterministic(key: Uint8Array, plaintext: Uint8Array, aad: Uint8Array): Promise<Uint8Array> {
        if (!ArrayBuffer.isView(key)) throw new Error('encryptDeterministic: key is not Uint8Array')
        if (!ArrayBuffer.isView(plaintext)) throw new Error('encryptDeterministic: plaintext is not Uint8Array')
        // Key separation: derive a separate key for HMAC nonce derivation
        const nonceKey = hkdf(nobleSha256, key, new Uint8Array(), encoder.encode('nonce-derivation'), 32)
        // Deterministic nonce: HMAC-SHA256(nonceKey, SHA-256(plaintext))[0:24]
        const contentDigest = nobleSha256(plaintext)
        const mac = nobleHmac(nobleSha256, nonceKey, contentDigest)
        const nonce = mac.slice(0, 24)
        const cipher = xchacha20poly1305(key, nonce, aad)
        const sealed = cipher.encrypt(plaintext)
        return concatBytes(nonce, sealed)
    }

    /**
     * Decrypt XChaCha20-Poly1305 ciphertext (Spec §4.2).
     * Input: nonce(24) || ciphertext+tag
     */
    async decrypt(key: Uint8Array, ciphertext: Uint8Array, aad?: Uint8Array): Promise<Uint8Array> {
        if (!ArrayBuffer.isView(key)) throw new Error('decrypt: key is not Uint8Array')
        if (!ArrayBuffer.isView(ciphertext)) throw new Error('decrypt: ciphertext is not Uint8Array')
        if (ciphertext.length < 24 + 16) throw new Error('decrypt: ciphertext too short')
        const nonce = ciphertext.slice(0, 24)
        const sealed = ciphertext.slice(24)
        const cipher = xchacha20poly1305(key, nonce, aad)
        return cipher.decrypt(sealed)
    }

    async hmac(key: Uint8Array, data: Uint8Array): Promise<Uint8Array> {
        return nobleHmac(nobleSha256, key, data)
    }

    async sign(seed: Uint8Array, message: Uint8Array): Promise<Uint8Array> {
        if (!ArrayBuffer.isView(seed)) throw new Error(`sign: seed is not Uint8Array`)
        if (!ArrayBuffer.isView(message)) throw new Error(`sign: message is not Uint8Array`)
        return ed.sign(message, seed)
    }

    async verify(pk: Uint8Array, message: Uint8Array, sig: Uint8Array): Promise<boolean> {
        return ed.verify(sig, message, pk)
    }

    /**
     * RFC 5869 HKDF using SHA-256 (Spec §4.2).
     * Uses @noble/hashes/hkdf for standards compliance.
     */
    async hkdf(ikm: Uint8Array, salt: Uint8Array, info: Uint8Array, len: number): Promise<Uint8Array> {
        return hkdf(nobleSha256, ikm, salt, info, len)
    }

    randomBytes(length: number): Uint8Array {
        return crypto.getRandomValues(new Uint8Array(length))
    }
}

export class DebounceSyncScheduler implements SyncScheduler {
    private debounceTimer: ReturnType<typeof setTimeout> | null = null
    private pollTimer: ReturnType<typeof setInterval> | null = null
    private mode: 'active' | 'suspended' = 'active'
    private notificationSource: 'polling' | 'websocket' = 'polling'
    private pollCallback: (() => Promise<void>) | null = null
    private basePollIntervalMs = 15_000

    private runBackgroundSyncCallback(callback: () => Promise<void>): void {
        void callback().catch(error => {
            sdkConsole.warn('[sync-scheduler] background sync failed', error)
        })
    }

    scheduleDebouncedSync(callback: () => Promise<void>): void {
        if (this.mode === 'suspended') return
        if (this.debounceTimer) clearTimeout(this.debounceTimer)
        this.debounceTimer = setTimeout(() => {
            this.runBackgroundSyncCallback(callback)
        }, 500)
    }

    startPolling(callback: () => Promise<void>, intervalMs: number): void {
        this.pollCallback = callback
        this.basePollIntervalMs = intervalMs
        this.applyPolling()
    }

    stopPolling(): void {
        if (this.pollTimer) {
            clearInterval(this.pollTimer)
            this.pollTimer = null
        }
    }

    setMode(mode: 'active' | 'suspended'): void {
        this.mode = mode
        this.applyPolling()
    }

    setNotificationSource(source: 'polling' | 'websocket'): void {
        this.notificationSource = source
        this.applyPolling()
    }

    dispose(): void {
        if (this.debounceTimer) clearTimeout(this.debounceTimer)
        this.stopPolling()
    }

    private applyPolling(): void {
        this.stopPolling()
        if (!this.pollCallback) return

        // When WebSocket is active, suppress polling entirely
        if (this.notificationSource === 'websocket') return

        const intervalMs = this.mode === 'active' ? this.basePollIntervalMs : this.basePollIntervalMs * 4
        this.pollTimer = setInterval(() => {
            if (!this.pollCallback) return
            this.runBackgroundSyncCallback(this.pollCallback)
        }, intervalMs)
    }
}

export class HttpRemoteStore implements RemoteStore {
    constructor(
        private readonly apiBaseUrl: string,
        private readonly capability: string,
        private readonly blobId?: string,
        private readonly authProvider?: import('./workspaceSecretV2').WorkspaceAuthProvider,
    ) { }

    /** Resolve authorization header — prefers auth provider token over raw capability. */
    private async authHeader(scope: 'read' | 'write', capabilityOverride?: string, workload: 'workspace-fs' | 'sqlite3mt' = 'workspace-fs'): Promise<HeadersInit> {
        if (this.authProvider && !capabilityOverride) {
            const token = await this.authProvider.getAccessToken(scope)
            return { authorization: `Bearer ${token.token}`, ...attributionHeaders(workload), ...getEphemeralHeaders() }
        }
        return { authorization: `Bearer ${capabilityOverride ?? this.capability}`, ...attributionHeaders(workload), ...getEphemeralHeaders() }
    }

    async getHeads(blobId: string, signal?: AbortSignal, capability?: string, lane?: string): Promise<import('./types').HeadsResult> {
        const laneQuery = lane != null ? `?lane=${encodeURIComponent(lane)}` : ''
        const url = `${this.apiBaseUrl}/blobs/${encodeURIComponent(blobId)}/heads${laneQuery}`
        const hdrs = await this.authHeader('read', capability, lane != null ? 'sqlite3mt' : 'workspace-fs')
        const res = await fetchWithRetry(() =>
            fetch(url, {
                headers: hdrs,
                cache: 'no-cache',
                signal
            }),
            { url, method: 'GET' },
        )
        if (!res.ok) throw new Error(`Failed getHeads ${res.status}`)
        const text = await res.text()
        let payload: { heads?: string[]; prefetchHints?: string[] } | string[]
        try {
            payload = JSON.parse(text)
        } catch {
            throw new Error(`getHeads: server returned non-JSON (url=${url}, body=${text.slice(0, 200)})`)
        }
        if (Array.isArray(payload)) return { heads: payload }
        return { heads: payload.heads ?? [], prefetchHints: payload.prefetchHints ?? undefined }
    }

    async getObject(hash: string, signal?: AbortSignal, capability?: string, blobId?: string): Promise<Uint8Array | null> {
        const effectiveBlobId = blobId ?? this.blobId
        const blobIdPath = effectiveBlobId ? `/blobs/${encodeURIComponent(effectiveBlobId)}` : ''
        const hdrs = await this.authHeader('read', capability)
        const url = `${this.apiBaseUrl}${blobIdPath}/objects/${encodeURIComponent(hash)}`
        const res = await fetchWithRetry(() =>
            fetch(url, {
                headers: hdrs,
                cache: 'no-cache',
                signal
            }),
            { url, method: 'GET' },
        )
        if (res.status === 404) return null
        if (!res.ok) throw new Error(`Failed getObject ${res.status}`)
        return new Uint8Array(await res.arrayBuffer())
    }

    /**
     * Batch-fetch multiple objects via one or more POST /objects/batch requests.
     *
     * Replaces the previous per-hash parallel getObject() loop which produced noisy
     * browser-visible HTTP 404s for every absent hash — a hygiene violation described
     * in proposal §139. The server caps each batch request at 100 hashes, so we
     * normalize the requested set and split larger loads across multiple POSTs.
     *
     * @returns Map of hash → object bytes (only present hashes are included).
     */
    async getObjectsBatch(hashes: string[], signal?: AbortSignal, capability?: string, blobId?: string): Promise<Map<string, Uint8Array>> {
        const normalizedHashes = Array.from(new Set(hashes.filter(hash => hash.length > 0)))
        if (normalizedHashes.length === 0) return new Map()

        const effectiveBlobId = blobId ?? this.blobId
        const blobIdPath = effectiveBlobId ? `/blobs/${encodeURIComponent(effectiveBlobId)}` : ''
        const hdrs = await this.authHeader('read', capability)

        const map = new Map<string, Uint8Array>()
        for (let i = 0; i < normalizedHashes.length; i += OBJECT_BATCH_GET_LIMIT) {
            const chunk = normalizedHashes.slice(i, i + OBJECT_BATCH_GET_LIMIT)
            const url = `${this.apiBaseUrl}${blobIdPath}/objects/batch`
            const res = await fetchWithRetry(() =>
                fetch(url, {
                    method: 'POST',
                    headers: { ...hdrs, 'content-type': 'application/json' },
                    body: JSON.stringify({ hashes: chunk }),
                    cache: 'no-cache',
                    signal,
                }),
                { url, method: 'POST' },
            )

            if (!res.ok) {
                throw new Error(`Failed getObjectsBatch ${res.status}`)
            }

            let payload: { objects: Record<string, string>; missing: string[] }
            try {
                payload = await res.json() as { objects: Record<string, string>; missing: string[] }
            } catch {
                throw new Error('getObjectsBatch: server returned non-JSON')
            }

            for (const [hash, base64] of Object.entries(payload.objects)) {
                const binaryString = atob(base64)
                const bytes = new Uint8Array(binaryString.length)
                for (let j = 0; j < binaryString.length; j++) {
                    bytes[j] = binaryString.charCodeAt(j)
                }
                map.set(hash, bytes)
            }
        }
        return map
    }

    async putObject(hash: string, bytes: Uint8Array, signal?: AbortSignal, capability?: string, blobId?: string): Promise<void> {
        const effectiveBlobId = blobId ?? this.blobId
        const blobIdPath = effectiveBlobId ? `/blobs/${encodeURIComponent(effectiveBlobId)}` : ''
        const hdrs = await this.authHeader('write', capability)
        const url = `${this.apiBaseUrl}${blobIdPath}/objects/${encodeURIComponent(hash)}`
        const res = await fetchWithRetry(() =>
            fetch(url, {
                method: 'PUT',
                headers: {
                    ...hdrs,
                    'content-type': 'application/octet-stream',
                    'x-secureblob-content-sha256': sha256Hex(bytes),
                },
                body: toAB(bytes),
                signal
            }),
            { url, method: 'PUT' },
        )
        if (!res.ok) throw new Error(`Failed putObject ${res.status}`)
    }

    async putObjectsBatch(entries: Array<{ hash: string; bytes: Uint8Array }>, signal?: AbortSignal, capability?: string, blobId?: string): Promise<void> {
        await Promise.all(entries.map(entry => this.putObject(entry.hash, entry.bytes, signal, capability, blobId)))
    }

    async updateHead(blobId: string, base: string[], newHead: string, signal?: AbortSignal, capability?: string, referencedObjects?: string[], prefetchHints?: string[], lane?: string): Promise<HeadUpdateResult> {
        const hdrs = await this.authHeader('write', capability, lane != null ? 'sqlite3mt' : 'workspace-fs')
        const laneQuery = lane != null ? `?lane=${encodeURIComponent(lane)}` : ''
        const url = `${this.apiBaseUrl}/blobs/${encodeURIComponent(blobId)}/heads${laneQuery}`
        const res = await fetchWithRetry(() =>
            fetch(url, {
                method: 'POST',
                headers: { ...hdrs, 'content-type': 'application/json' },
                body: JSON.stringify({
                    base, new: newHead,
                    ...(referencedObjects ? { referencedObjects } : {}),
                    ...(prefetchHints ? { prefetchHints } : {}),
                }),
                signal
            }),
            { url, method: 'POST' },
        )
        if (res.ok) {
            sdkConsole.debug(`[secureblob:http] updateHead OK blob=${blobId.slice(0, 12)} base=[${base.map(h => h.slice(0, 8))}] new=${newHead.slice(0, 8)}`)
            return { ok: true }
        }
        if (res.status === 409) {
            const payload = await res.json() as { heads?: string[]; remoteHead?: string }
            const remoteHead = payload.remoteHead ?? payload.heads?.[0] ?? ''
            sdkConsole.warn(`[secureblob:http] updateHead 409 CAS_MISMATCH blob=${blobId.slice(0, 12)} base=[${base.map(h => h.slice(0, 8))}] new=${newHead.slice(0, 8)} serverHead=${remoteHead.slice(0, 8)}`)
            return { ok: false, code: 'CAS_MISMATCH', remoteHead }
        }
        if (res.status === 403) return { ok: false, code: 'FORBIDDEN', message: 'forbidden' }
        if (res.status === 404) return { ok: false, code: 'NOT_FOUND', message: 'not_found' }
        throw new Error(`Failed updateHead ${res.status}`)
    }

    async softDeleteBlob(blobId: string, signal?: AbortSignal, capability?: string): Promise<void> {
        const hdrs = await this.authHeader('write', capability)
        const url = `${this.apiBaseUrl}/blobs/${encodeURIComponent(blobId)}`
        const res = await fetchWithRetry(() =>
            fetch(url, {
                method: 'DELETE',
                headers: hdrs,
                signal
            }),
            { url, method: 'DELETE' },
        )
        if (!res.ok && res.status !== 204) throw new Error(`Failed softDeleteBlob ${res.status}`)
    }
}

export class MemoryRemoteStore implements RemoteStore {
    heads = new Map<string, string[]>()
    objects = new Map<string, Uint8Array>()

    async getHeads(blobId: string, _signal?: AbortSignal, _capability?: string, lane?: string): Promise<import('./types').HeadsResult> {
        return { heads: this.heads.get(lane == null ? blobId : `${blobId}|lane|${lane}`) ?? [] }
    }
    async getObject(hash: string): Promise<Uint8Array | null> { return this.objects.get(hash) ?? null }
    async getObjectsBatch(hashes: string[], signal?: AbortSignal, capability?: string): Promise<Map<string, Uint8Array>> {
        return new Map(hashes.map(hash => [hash, this.objects.get(hash)]).filter(([, v]) => v) as Array<[string, Uint8Array]>)
    }
    async putObject(hash: string, bytes: Uint8Array): Promise<void> { this.objects.set(hash, bytes) }
    async putObjectsBatch(entries: Array<{ hash: string; bytes: Uint8Array }>, signal?: AbortSignal, capability?: string): Promise<void> {
        entries.forEach(entry => this.objects.set(entry.hash, entry.bytes))
    }
    async updateHead(blobId: string, base: string[], newHead: string, _signal?: AbortSignal, _capability?: string, _referencedObjects?: string[], _prefetchHints?: string[], lane?: string): Promise<HeadUpdateResult> {
        const headKey = lane == null ? blobId : `${blobId}|lane|${lane}`
        const remote = this.heads.get(headKey) ?? []
        const sameBase = remote.length === base.length && remote.every((item, index) => item === base[index])
        if (!sameBase) return { ok: false, code: 'CAS_MISMATCH', remoteHead: remote[0] ?? '' }
        this.heads.set(headKey, [newHead])
        return { ok: true }
    }
    async softDeleteBlob(_blobId: string): Promise<void> { /* no-op for in-memory */ }
}
